/**
 * `_auditEvent.recorded` decoding.
 *
 * The field held TWO types until 2026-07-29: `libs/stc-alerts` wrote a
 * native Firestore Timestamp, while the compliance-audit writers wrote an
 * ISO string via `.isoformat()`. Firestore orders by TYPE before value, so
 * the two populations formed disjoint sorted runs — and this loader used to
 * accept only strings:
 *
 *     recorded: typeof data.recorded === "string" ? data.recorded : null
 *
 * which rendered every alert row's time as "—" (136 of 205 docs in prod).
 *
 * Writers are unified on native timestamps and the history is backfilled,
 * but `_auditEvent` is append-only: anything that escaped the backfill must
 * still display. So decode both, permanently.
 *
 * Kept in its own module (like `person-name.ts`) so tests import the pure
 * function without pulling in the Firestore client init chain.
 */

/** Minimal structural type for a Firestore Timestamp. */
interface TimestampLike {
  toDate(): Date;
}

function isTimestampLike(value: unknown): value is TimestampLike {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  );
}

/**
 * Normalise a stored `recorded` value to an ISO-8601 string.
 *
 * Duck-types the Timestamp rather than using `instanceof`, so it holds for
 * the modular SDK class and for a plain decoded `{seconds, nanoseconds}`
 * object alike. Returns null only when there is genuinely nothing usable.
 */
export function parseRecorded(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (isTimestampLike(value)) {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
}
